import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Trash2, Search } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHead } from "./AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const STATUSES = {
  baru: ["Baru", "bg-amber-100 text-amber-800"],
  dihubungi: ["Dihubungi", "bg-sky-100 text-sky-800"],
  dokumen_diterima: ["Dokumen Diterima", "bg-indigo-100 text-indigo-800"],
  proses: ["Proses LPJK/OSS", "bg-violet-100 text-violet-800"],
  selesai: ["Selesai", "bg-emerald-100 text-emerald-800"],
  batal: ["Batal", "bg-slate-200 text-slate-600"],
};

export const StatusBadge = ({ status }) => {
  const [l, c] = STATUSES[status] || [status, "bg-slate-100"];
  return <span className={`px-2 py-1 font-mono text-[10px] uppercase tracking-wider ${c}`}>{l}</span>;
};

const waHref = (phone, name) => {
  let d = (phone || "").replace(/\D/g, "");
  if (d.startsWith("0")) d = "62" + d.slice(1);
  return `https://wa.me/${d}?text=${encodeURIComponent(`Halo ${name}, kami dari PT Rikonstruksi Utama menindaklanjuti permohonan konsultasi Anda.`)}`;
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/leads", { params: { status: status === "all" ? undefined : status, source: source === "all" ? undefined : source } }).then((r) => setLeads(r.data));
  useEffect(() => { load(); }, [status, source]); // eslint-disable-line

  const patch = async (id, data) => {
    try {
      const { data: l } = await api.patch(`/admin/leads/${id}`, data);
      setLeads((ls) => ls.map((x) => (x.id === id ? l : x)));
      if (detail?.id === id) setDetail(l);
      toast.success("Lead diperbarui");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus lead ini?")) return;
    await api.delete(`/admin/leads/${id}`);
    setLeads((ls) => ls.filter((x) => x.id !== id));
  };
  const shown = leads.filter((l) => `${l.name} ${l.company} ${l.phone} ${l.service}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-leads">
      <PageHead eyebrow="CRM" title="Lead & Inquiry Tracker" desc="Prospek dari formulir website dan eskalasi Chatbot AI (high-intent).">
        <div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." data-testid="leads-search-input" className="border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm outline-none" /></div>
        <Select value={source} onValueChange={setSource}><SelectTrigger className="w-36 bg-white" data-testid="leads-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua sumber</SelectItem><SelectItem value="website">Website</SelectItem><SelectItem value="chatbot">Chatbot</SelectItem></SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44 bg-white" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua status</SelectItem>{Object.entries(STATUSES).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      </PageHead>
      <div className="mb-4 flex flex-wrap gap-2">
        {Object.entries(STATUSES).map(([k, [l, c]]) => <span key={k} className={`px-3 py-1.5 text-xs ${c}`}>{l}: <b>{leads.filter((x) => x.status === k).length}</b></span>)}
      </div>
      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <tr><th className="p-3">Prospek</th><th className="p-3">Layanan</th><th className="p-3">Sumber</th><th className="p-3">Masuk</th><th className="p-3">Status</th><th className="p-3 text-right">Aksi</th></tr>
          </thead>
          <tbody className="divide-y">
            {shown.map((l) => (
              <tr key={l.id} className="hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                <td className="cursor-pointer p-3" onClick={() => setDetail(l)}><p className="font-semibold text-[#0F2B48]">{l.name}</p><p className="text-xs text-slate-500">{l.company || "-"} · {l.phone || "no. belum ada"}</p></td>
                <td className="p-3">{l.service}{l.intent_labels?.length > 0 && <p className="text-[11px] text-emerald-700">{l.intent_labels.join(", ")}</p>}</td>
                <td className="p-3"><span className={`px-2 py-0.5 font-mono text-[10px] uppercase ${l.source === "chatbot" ? "bg-[#0F2B48] text-white" : "bg-slate-100"}`}>{l.source}</span></td>
                <td className="p-3 text-xs text-slate-500">{fmtDate(l.created_at)}</td>
                <td className="p-3">
                  <Select value={l.status} onValueChange={(v) => patch(l.id, { status: v })}>
                    <SelectTrigger className="h-8 w-44 text-xs" data-testid={`lead-status-select-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUSES).map(([k, [lb]]) => <SelectItem key={k} value={k}>{lb}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    {l.phone && <a href={waHref(l.phone, l.name)} target="_blank" rel="noreferrer" className="bg-emerald-500 p-2 text-white hover:bg-emerald-600" data-testid={`lead-wa-${l.id}`}><MessageCircle size={14} /></a>}
                    <button onClick={() => del(l.id)} className="border p-2 text-rose-600 hover:bg-rose-50" data-testid={`lead-delete-${l.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Belum ada lead.</td></tr>}
          </tbody>
        </table>
      </div>
      <LeadDetail lead={detail} onClose={() => setDetail(null)} onSave={patch} />
    </div>
  );
}

function LeadDetail({ lead, onClose, onSave }) {
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  useEffect(() => { setNotes(lead?.notes || ""); setPhone(lead?.phone || ""); }, [lead]);
  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none" data-testid="lead-detail-dialog">
        <DialogHeader><DialogTitle>{lead?.name}</DialogTitle></DialogHeader>
        {lead && (
          <div className="space-y-3 text-sm">
            <p><b>Layanan:</b> {lead.service} · <b>Sumber:</b> {lead.source}</p>
            {lead.email && <p><b>Email:</b> {lead.email}</p>}
            <div className="max-h-48 overflow-y-auto whitespace-pre-wrap border bg-slate-50 p-3 text-xs">{lead.message || "-"}</div>
            <label className="block text-xs font-semibold">No. WhatsApp<input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full border px-3 py-2" data-testid="lead-detail-phone" /></label>
            <label className="block text-xs font-semibold">Catatan internal<textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full border px-3 py-2" data-testid="lead-detail-notes" /></label>
            <button onClick={() => onSave(lead.id, { notes, phone })} className="w-full bg-[#0F2B48] py-2.5 text-sm font-bold text-white hover:bg-[#D97706]" data-testid="lead-detail-save">Simpan</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
